import React from 'react';
import { useConsent } from 'react-hook-consent';
import { isReactNativeWebView } from '../../util/isReactNativeWebView';

const LABELS = ['Settings', 'Einstellungen', 'Ρυθμίσεις'];
const norm = s =>
  (s || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const isVisible = el => {
  if (!el) return false;
  const s = window.getComputedStyle(el);
  if (s.display === 'none' || s.visibility === 'hidden' || parseFloat(s.opacity) === 0)
    return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
};

const closestClickable = el => el?.closest?.('button, a, [role="button"], [onclick]') || el;

const findSettingsEl = () => {
  const labels = LABELS.map(norm);
  const matches = v => labels.some(l => norm(v).includes(l));

  // Likely clickable nodes first
  const primary = document.querySelectorAll('button, a, [role="button"]');
  for (const el of primary) {
    const txt = el.textContent || el.innerText || '';
    const aria = el.getAttribute?.('aria-label') || '';
    if ((txt && matches(txt)) || (aria && matches(aria))) return closestClickable(el);
  }

  // Wider sweep
  const all = document.querySelectorAll('*');
  for (const el of all) {
    const txt = el.textContent || el.innerText || '';
    const aria = el.getAttribute?.('aria-label') || '';
    if ((txt && matches(txt)) || (aria && matches(aria))) return closestClickable(el);
  }

  // Heuristic data-attrs (safe if absent)
  return (
    document.querySelector(
      '[data-consent-settings], [data-cookie-settings], [data-rhc-settings]'
    ) || null
  );
};

const waitFor = async (fn, { timeout = 4000, interval = 120 } = {}) => {
  const start = Date.now();
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const result = fn();
    if (result) return result;
    if (Date.now() - start >= timeout) return null;
    // eslint-disable-next-line no-await-in-loop
    await new Promise(r => setTimeout(r, interval));
  }
};

const ConsentOpenBridge = () => {
  if (isReactNativeWebView()) {
    return null;
  }
  const ctx = (typeof useConsent === 'function' ? useConsent() : {}) || {};

  React.useEffect(() => {
    window.__openConsent = async () => {
      // 1) Use official API if present (opens the modal directly)
      if (typeof ctx.openModal === 'function') return void ctx.openModal();
      if (typeof ctx.setIsModalOpen === 'function') return void ctx.setIsModalOpen(true);

      // 2) If Settings is already in DOM, click it
      let settingsEl = findSettingsEl();
      if (settingsEl && isVisible(settingsEl)) {
        settingsEl.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
        return;
      }

      // 3) Ensure the banner is visible, then wait for Settings and click it
      if (typeof ctx.toggleBanner === 'function') {
        ctx.toggleBanner(true);
      }

      settingsEl = await waitFor(() => {
        const el = findSettingsEl();
        return el && isVisible(el) ? el : null;
      });

      if (settingsEl) {
        settingsEl.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
        return;
      }

      // Optional diagnostic if nothing worked
      // eslint-disable-next-line no-console
      console.warn('[ConsentOpenBridge] Could not open the Settings modal.');
      window.dispatchEvent(new CustomEvent('react-hook-consent:open'));
    };

    return () => {
      try {
        delete window.__openConsent;
      } catch {
        window.__openConsent = undefined;
      }
    };
  }, [ctx]);

  return null;
};

export default ConsentOpenBridge;
